import { useState, useEffect } from 'react';

export interface PhoneNumberInputProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * Phone number input component with auto-formatting for US phone numbers
 * Formats as user types: (555) 555-5555
 */
export function PhoneNumberInput({
  value,
  onChange,
  error,
  required = false,
  disabled = false,
  placeholder = '(555) 555-5555',
}: PhoneNumberInputProps) {
  const [displayValue, setDisplayValue] = useState('');

  // Format phone number for display: (555) 555-5555
  const formatPhoneNumberInput = (input: string): string => {
    // Remove all non-digit characters
    let digits = input.replace(/\D/g, '');

    // Strip leading 1 (US country code) if present
    if (digits.length === 11 && digits[0] === '1') {
      digits = digits.substring(1);
    }

    // Return empty if no digits
    if (digits.length === 0) return '';

    // Format based on number of digits (max 10)
    if (digits.length <= 3) {
      return `(${digits}`;
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  // Update display value when controlled value changes
  useEffect(() => {
    if (value) {
      setDisplayValue(formatPhoneNumberInput(value));
    } else {
      setDisplayValue('');
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;

    // Format the input for display
    const formatted = formatPhoneNumberInput(input);
    setDisplayValue(formatted);

    // Extract digits only for the value
    const digits = input.replace(/\D/g, '');

    // Call onChange with formatted value (or empty string if no digits)
    onChange(digits.length > 0 ? formatted : '');
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.5rem',
    fontSize: '1rem',
    border: error ? '2px solid var(--error-color)' : '1px solid var(--border-color)',
    borderRadius: 'var(--radius)',
    backgroundColor: disabled ? 'var(--bg-secondary)' : 'var(--bg-primary)',
    color: disabled ? 'var(--text-secondary)' : 'var(--text-primary)',
    cursor: disabled ? 'not-allowed' : 'text',
  };

  const errorStyle: React.CSSProperties = {
    color: 'var(--error-color)',
    fontSize: '0.875rem',
    marginTop: '0.25rem',
  };

  return (
    <div>
      <input
        type="tel"
        value={displayValue}
        onChange={handleChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        style={inputStyle}
        maxLength={17} // +1 (555) 555-5555 = 17 characters (supports autofill with country code)
        inputMode="numeric"
        autoComplete="tel"
      />
      {error && <div style={errorStyle}>{error}</div>}
    </div>
  );
}

export default PhoneNumberInput;
